import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../contexts/AppSettingsContext'
import { authAPI } from '../services/auth'
import LiveChatEmbed from '../components/LiveChatEmbed'
import './Login.css'

function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [captchaId, setCaptchaId] = useState('')
  const [captchaImage, setCaptchaImage] = useState('')
  const [captcha, setCaptcha] = useState('')
  const [captchaLoading, setCaptchaLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [otpStep, setOtpStep] = useState(false)
  const [challengeId, setChallengeId] = useState('')
  const [otpCode, setOtpCode] = useState('')

  const { login, verifyOtp } = useAuth()
  const { settings } = useAppSettings()
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true)
    try {
      const response = await authAPI.getCaptcha()
      setCaptchaId(response.data.id)
      setCaptchaImage(response.data.image)
      setCaptcha('')
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load captcha')
      setCaptchaId('')
      setCaptchaImage('')
    } finally {
      setCaptchaLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCaptcha()
  }, [loadCaptcha])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    if (otpStep) {
      const result = await verifyOtp(challengeId, otpCode)
      if (result.success) {
        navigate(from, { replace: true })
      } else {
        setError(result.error)
      }
      setIsLoading(false)
      return
    }

    const result = await login(username, password, captchaId, captcha)

    if (result.success) {
      navigate(from, { replace: true })
    } else if (result.requiresOtp) {
      setOtpStep(true)
      setChallengeId(result.challengeId)
      setOtpCode('')
    } else {
      setError(result.error)
      await loadCaptcha()
    }

    setIsLoading(false)
  }

  const canSubmit = otpStep
    ? otpCode.length >= 6 && !isLoading
    : username && password && captcha && captchaId && !isLoading && !captchaLoading

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>{settings.appName}</h1>
          <p>{otpStep ? 'Enter the login code sent to your email' : 'Sign in to access the dashboard'}</p>
        </div>

        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {!otpStep && (
            <>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                  autoFocus
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="captcha">Captcha</label>
                <div className="captcha-row">
                  <div className="captcha-image-wrap">
                    {captchaImage ? (
                      <img
                        src={captchaImage}
                        alt="Captcha challenge"
                        className="captcha-image"
                      />
                    ) : (
                      <div className="captcha-image captcha-image-placeholder">
                        {captchaLoading ? 'Loading…' : 'Unavailable'}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="captcha-refresh"
                    onClick={loadCaptcha}
                    disabled={captchaLoading}
                    aria-label="Refresh captcha"
                    title="Refresh captcha"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7c2.76 0 5 2.24 5 5a5 5 0 0 1-8.9 3.1L6.24 16.9A8 8 0 1 0 17.65 6.35Z"
                      />
                    </svg>
                  </button>
                </div>
                <input
                  id="captcha"
                  type="text"
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value.toUpperCase())}
                  placeholder="Enter the characters shown"
                  required
                  autoComplete="off"
                  spellCheck="false"
                  maxLength={5}
                  inputMode="text"
                />
              </div>
            </>
          )}

          {otpStep && (
            <div className="form-group">
              <label htmlFor="otpCode">Email OTP</label>
              <input
                id="otpCode"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                required
                autoFocus
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
              />
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={!canSubmit}
          >
            {isLoading ? (otpStep ? 'Verifying...' : 'Signing in...') : (otpStep ? 'Verify code' : 'Sign In')}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Powered By{' '}
            <a
              href="https://cobrasoft.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cobra Softwares Inc.
            </a>
          </p>
        </div>
      </div>
      <LiveChatEmbed html={settings.liveChatEmbed} />
    </div>
  )
}

export default Login
